/**
 * Drizzle query builders + query-key factory for the academy's ORGANIZATION layer
 * (org_settings / campus / room). Local-first consumption over PowerSync (`academy_org`
 * stream). Writes go through the write-path (`db/sync.rs`), which applies authz by
 * capability (org_rbac) — the client is never the authority.
 */

import {
  and,
  asc,
  campus,
  eq,
  isNull,
  org_settings,
  type PowerSyncDrizzleDB,
  room,
} from "@space/powersync";

/** Cache keys for the organization layer (TanStack Query). */
export const academyOrgQueryKeys = {
  all: ["academy-org"] as const,
  settings: (orgId: string | undefined) => ["academy-org", "settings", orgId] as const,
  campuses: (orgId: string | undefined) => ["academy-org", "campuses", orgId] as const,
  rooms: (campusId: string | undefined) => ["academy-org", "rooms", campusId] as const,
  roomsByOrg: (orgId: string | undefined) => ["academy-org", "rooms-by-org", orgId] as const,
};

/** An organization's (singleton) settings. id = organization_id. */
export function getOrgSettingsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(org_settings).where(eq(org_settings.id, orgId)).limit(1);
}

/** An organization's active (non-deleted) campuses. */
export function getCampusesQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(campus)
    .where(and(eq(campus.organization_id, orgId), isNull(campus.deleted_at)))
    .orderBy(asc(campus.name));
}

/** A campus's active rooms. */
export function getRoomsByCampusQuery(db: PowerSyncDrizzleDB, campusId: string) {
  return db
    .select()
    .from(room)
    .where(and(eq(room.campus_id, campusId), isNull(room.deleted_at)))
    .orderBy(asc(room.name));
}

/** Active rooms across the entire organization. */
export function getRoomsByOrgQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(room)
    .where(and(eq(room.organization_id, orgId), isNull(room.deleted_at)))
    .orderBy(asc(room.name));
}
